import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import CableIcon from "@mui/icons-material/Cable";
import { tokens } from "../../../theme";
import { useAppContext } from "../../../contexts/AppContext";
import {
  computeSelectionSignature,
  isPreCheckStale,
} from "../../../utils/mockSchemaValidation";
import { StatusBadge } from "../../../components/states";

// Map each pre-run check severity to the icon-tile accent. The pill itself is
// rendered by the shared StatusBadge (1.4) so all status pills read as one system.
const VARIANT_ICON_COLOR = {
  success: "var(--fx-success)",
  warning: "var(--fx-warning)",
  danger: "var(--fx-danger)",
  neutral: "var(--fx-text-muted)",
};

/**
 * Execute page — Pre-run Checks panel.
 *
 * ALWAYS renders. Three rows:
 *   1. Configuration — Saved (when currentConfig present).
 *   2. Schema validation — reflects schemaPreCheck state with stale detection.
 *   3. Access check — Pending (prototype, doesn't actually verify anything).
 *
 * Starting training first runs final schema validation and access checks.
 */
const PreRunChecksPanel = ({ currentConfig, isFinalChecking }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { schemaPreCheck, selectedSites } = useAppContext();

  const processingType = currentConfig?.dataset_config?.processing_type || null;
  const currentSignature = useMemo(
    () => computeSelectionSignature(processingType, selectedSites),
    [processingType, selectedSites]
  );

  // Derive schema validation row state. Three severities (pass / warn / fail)
  // mirror the backend data-review model; warn is non-blocking.
  const schemaState = (() => {
    if (isFinalChecking) return "checking";
    const r = schemaPreCheck?.result;
    if (!r) return "not_checked";
    const stale = isPreCheckStale(currentSignature, schemaPreCheck.signature);
    if (stale) return "stale";
    const overall = r.overall || (r.passed ? "pass" : "fail");
    const isFinal = r.source === "final";
    if (overall === "pass") return isFinal ? "final_passed" : "pre_passed";
    if (overall === "warn") return isFinal ? "final_warn" : "pre_warn";
    return isFinal ? "final_failed" : "pre_failed";
  })();

  const Row = ({ icon, label, variant = "neutral", statusLabel, note }) => (
    <Box sx={{ py: 1.1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '26px 1fr auto',
          alignItems: 'center',
          columnGap: 1.25,
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1,
            bgcolor: 'var(--fx-subtle)',
            border: '1px solid var(--fx-border)',
            color: VARIANT_ICON_COLOR[variant] || 'var(--fx-text-muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { fontSize: 15 },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 600, color: 'var(--fx-text-primary)', fontSize: '0.8125rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        <StatusBadge variant={variant} label={statusLabel} dot />
      </Box>
      {note && (
        <Typography
          sx={{
            mt: 0.6,
            ml: 4.5, // align with label column (26 + 1.25*8 = ~36px, use 4.5 = 36px)
            color: 'var(--fx-text-secondary)',
            fontSize: '0.72rem',
            lineHeight: 1.5,
          }}
        >
          {note}
        </Typography>
      )}
    </Box>
  );

  const schemaRow = (() => {
    switch (schemaState) {
      case "not_checked":
        return (
          <Row
            icon={<RuleIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Not checked yet"
            variant="neutral"
            note="Schema compatibility will be validated automatically when you start training."
          />
        );
      case "checking":
        return (
          <Row
            icon={<RuleIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Running…"
            variant="neutral"
            note="Running schema validation against the selected site datasets…"
          />
        );
      case "pre_passed":
        return (
          <Row
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Pre-check passed"
            variant="success"
            note="Final validation will run again before training starts."
          />
        );
      case "final_passed":
        return (
          <Row
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Validation passed"
            variant="success"
            note="Final validation passed."
          />
        );
      case "pre_warn":
        return (
          <Row
            icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Pre-check passed with warnings"
            variant="warning"
            note="Some sites raised warnings (non-blocking). Training may proceed; see the data review below."
          />
        );
      case "final_warn":
        return (
          <Row
            icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Passed with warnings"
            variant="warning"
            note="Validation passed with non-blocking warnings. See the Federated Data Review below."
          />
        );
      case "pre_failed":
        return (
          <Row
            icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Pre-check found blocking issues"
            variant="danger"
            note="Training will not start unless these are resolved. View details below if available."
          />
        );
      case "final_failed":
        return (
          <Row
            icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Validation failed"
            variant="danger"
            note="Training was not started. See the validation details card below."
          />
        );
      case "stale":
        return (
          <Row
            icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
            label="Schema validation"
            statusLabel="Selection changed since last check"
            variant="warning"
            note="Final validation will run when you start training."
          />
        );
      default:
        return null;
    }
  })();

  return (
    <Card sx={{ mb: 2, border: '1px solid var(--fx-border)', borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)' }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: 1,
            bgcolor: 'rgba(0,135,180,0.10)', color: '#0087B4',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <CircleNotificationsIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: 'var(--fx-text-primary)', fontSize: '0.9375rem', letterSpacing: '-0.005em' }}>
            Pre-run checks
          </Typography>
        </Stack>

        <Stack divider={<Box sx={{ borderTop: '1px solid var(--fx-border)' }} />}>
          <Row
            icon={
              currentConfig ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} />
              ) : (
                <ErrorOutlineIcon sx={{ fontSize: 16 }} />
              )
            }
            label="Configuration"
            statusLabel={currentConfig ? "Saved" : "Not saved"}
            variant={currentConfig ? "success" : "danger"}
            note={
              currentConfig
                ? undefined
                : "Save a configuration on the Configurations page before starting training."
            }
          />
          {schemaRow}
          <Row
            icon={<CableIcon sx={{ fontSize: 16 }} />}
            label="Access check"
            statusLabel="Pending"
            variant="warning"
            note="Site access and ACLs are checked automatically when training starts."
          />
        </Stack>

        <Box
          sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: '1px dashed var(--fx-border)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
          }}
        >
          <ScheduleIcon sx={{ fontSize: 13, color: 'var(--fx-text-muted)', mt: '2px' }} />
          <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.7rem', lineHeight: 1.5 }}>
            Starting training will first run schema validation and access checks. Training only proceeds if both pass.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PreRunChecksPanel;
